<template>
    <page :tabs="tabs" :busy="busy" :show-nav="!isMobile">
        <div v-if="isMobile" class="mt-1" />
        <grid
            ref="grid"
            :column-defs="columnDefs"
            :row-data="rowData"
            :is-auto-width="true"
            :is-search="false"
            :statusBar="statusBar"
            :getRowStyle="getRowStyle"
            :pinned-bottom-row-data="pinnedBottomRowData"
            grid-theme="ag-theme-alpine-report"
        >
            <date-range-picker
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleDaterangeChange"
            />
            <div class="d-none d-sm-flex align-items-baseline mr-1 mb-1 filter-properties">
                <search-multiselect
                    v-model="filterProperties"
                    :options="filterPropertyOptions"
                    :icon="['fas', 'hotel']"
                    :responsiveness="true"
                    variant="primary"
                    placeholder="Properties"
                />
            </div>
            <export-to-excel-button :href="exportExcelHref"></export-to-excel-button>
            <exportToPDFButton :href="exportPdfHref" :pdfCacheKey="pdfCacheKey" />
            <b-button-group>
                <more-filters-dropdown class="income-detail-dropdown">
                    <template v-slot:default="{ handleDropdownShow, handleDropdownHide }">
                        <search-multiselect
                            class="mr-1 d-sm-none d-flex"
                            v-model="filterProperties"
                            :options="filterPropertyOptions"
                            :icon="['fas', 'hotel']"
                            variant="primary"
                            placeholder="Properties"
                            @show="handleDropdownShow"
                            @hide="handleDropdownHide"
                        />
                        <form-square-switch
                            v-for="filter in incomeDetailFilters"
                            :key="filter.key"
                            :labels="{
                                checked: filter.labels,
                                unchecked: filter.labels,
                            }"
                            v-model="filterValues[filter.key]"
                            @change="refresh"
                            class="mb-1 income-detail-toggle"
                            :sync="true"
                        />
                    </template>
                </more-filters-dropdown>
            </b-button-group>
            <report-settings-modal :modalId="reportSettingsModalId" :initialValues="reportSettings" class="ml-1" />
        </grid>
    </page>
</template>

<script>
    import { mapGetters } from 'vuex';
    import format from 'date-fns/format';
    import API from '../../../api';
    import Page from '../../generic/page/page.vue';
    import Grid from '../../generic/grid/grid.vue';
    import Modal from '../../generic/modal/modal.vue';
    import ResponsiveDropdown from '../../generic/responsive-dropdown/responsive-dropdown.vue';
    import MoreFiltersDropdown from '../../generic/more-filters-dropdown/more-filters-dropdown.vue';
    import SearchMultiselect from '../../generic/search-multiselect/search-multiselect.vue';
    import FormSquareSwitch from '../../generic/form-square-switch/form-square-switch.vue';
    import ExportToExcelButton from '../../generic/export-to-excel-button/export-to-excel-button.vue';
    import DateRangePicker, {
        nameToRange,
        TODAY,
        YESTERDAY,
        THIS_MONTH,
        LAST_MONTH,
        TOMORROW,
        THIS_WEEK,
        NEXT_WEEK,
        NEXT_MONTH,
        THIS_YEAR,
        LAST_YEAR,
        LAST_WEEK,
    } from '../../generic/date-range-picker/date-range-picker.vue';
    import { REPORT_INCOME_DETAIL_ENDPOINT, REPORT_INCOME_DETAIL_PROPERTIES_ENDPOINT } from '../../../endpoints';
    import { getPinnedBottomRowData, getTotalDataRow } from './helpers';
    import ReportSummaryBar from '../report-summary-bar.vue';
    import { removeScrollMixin } from '../../../mixins/remove-scroll-mixin';
    import { isMediumScreen } from '../../../utils/screens';
    import ReportSettingsModal, {
        DATE_PERIODS_INPUT,
        MULTI_SELECT_INPUT,
        SWITCH_INPUT,
    } from '../report-settings/report-settings-modal.vue';
import { defineAsyncComponent } from 'vue';

    const exportToPDFButton = defineAsyncComponent(() =>
        import(
            /* webpackChunkName: "lx-export-to-pdf-button" */
            '../../generic/export-to-pdf-button/export-to-pdf-button.vue'
        ))

    export default {
        name: 'IncomeDetailReport',
        components: {
            Page,
            Grid,
            Modal,
            ResponsiveDropdown,
            DateRangePicker,
            reportSummaryBar: ReportSummaryBar,
            exportToPDFButton,
            ExportToExcelButton,
            SearchMultiselect,
            FormSquareSwitch,
            MoreFiltersDropdown,
            ReportSettingsModal,
        },
        mixins: [removeScrollMixin],
        data() {
            return {
                title: 'Income Detail',
                tabs: [
                    {
                        title: 'Income Detail',
                        route: '/app/reports/income-detail/',
                        active: true,
                    },
                ],
                dateRange: nameToRange(LAST_MONTH),
                predefinedRanges: [
                    YESTERDAY,
                    TODAY,
                    TOMORROW,
                    LAST_WEEK,
                    THIS_WEEK,
                    NEXT_WEEK,
                    LAST_MONTH,
                    THIS_MONTH,
                    NEXT_MONTH,
                    THIS_YEAR,
                    LAST_YEAR,
                ],
                dateRequestFormat: 'yyyy-MM-dd',
                busy: false,
                getRowStyle: null,
                gridApi: null,
                rowData: [],
                average: [],
                totals: [],
                filterProperties: [],
                filterPropertyOptions: [],
                filterValues: {
                    showRooms: false,
                    showDisabledProperties: false,
                    showPropertiesWithNoActivity: true,
                },
                windowWidth: 0,
                windowHeight: 0,
                statusBar: null,
                pdfCacheKey: null,
                reportSettingsModalId: 'report-settings-modal',
            };
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat', 'userCurrencySymbol']),
            columnDefs() {
                return [
                    {
                        field: 'rental_property.name',
                        headerName: 'Property/Room',
                        valueGetter: ({ data, node }) => {
                            return node.rowPinned ? data.name : data.room ? data.room.name : data.rental_property.name;
                        },
                        cellStyle: ({ data, node }) => {
                            return node.rowPinned || !data.room ? { 'font-weight': 'bold' } : null;
                        },
                        width: isMediumScreen() ? undefined : 80,
                        autoWidthExclude: this.isMobile,
                        pinned: 'left',
                    },
                    {
                        field: 'available_nts',
                        headerName: 'Nts Avail',
                        headerClass: 'ag-right-aligned-header',
                        cellClass: 'ag-right-aligned-cell',
                    },
                    {
                        field: 'booked_nts',
                        headerName: 'Nts Booked',
                        headerClass: 'ag-right-aligned-header',
                        cellClass: 'ag-right-aligned-cell',
                    },
                    {
                        field: 'blocked_nts',
                        headerName: 'Nts Blocked',
                        headerClass: 'ag-right-aligned-header',
                        cellClass: 'ag-right-aligned-cell',
                    },
                    {
                        field: 'occupied_nts',
                        headerName: 'Nts Occupied',
                        headerClass: 'ag-right-aligned-header',
                        cellClass: 'ag-right-aligned-cell',
                    },
                    {
                        field: 'booked_percent',
                        headerName: 'Guest Occ %',
                        type: 'amountColumn',
                        cellRendererParams: {
                            percentage: true,
                        },
                        minWidth: 100,
                    },
                    {
                        field: 'occupied_percent',
                        headerName: 'Total Occ %',
                        type: 'amountColumn',
                        cellRendererParams: {
                            percentage: true,
                        },
                        minWidth: 95,
                    },
                    {
                        field: 'rental_charges',
                        headerName: 'Rental Charges',
                        type: 'amountColumn',
                    },
                    {
                        field: 'avg_rental_charges',
                        headerName: 'Avg Per Day',
                        type: 'amountColumn',
                        minWidth: 95,
                    },
                    {
                        field: 'fee_service_revenue',
                        headerName: 'Fee/Service Revenue',
                        type: 'amountColumn',
                        minWidth: 95,
                    },
                    {
                        field: 'avg_fee_service_revenue',
                        headerName: 'Avg Per Day',
                        type: 'amountColumn',
                        minWidth: 95,
                    },
                    {
                        field: 'tax',
                        headerName: 'Tax',
                        type: 'amountColumn',
                    },
                    {
                        field: 'total_revenues',
                        headerName: 'Total Revenues',
                        type: 'amountColumn',
                    },
                    {
                        field: '',
                        headerName: '',
                        autoWidthExclude: true,
                        pdfExportOptions: {
                            skipColumn: true,
                        },
                        sortable: false,
                    },
                ];
            },
            pinnedBottomRowData() {
                return getPinnedBottomRowData(this.average, this.totals);
            },
            exportPdfHref() {
                let url = '/app/reports/income-detail/row-data/pdf/';
                url += `?from_date=${this.startDateRequest}`;
                url += `&to_date=${this.endDateRequest}`;
                url += `&show_rooms=${this.filterValues.showRooms}`;
                url += `&show_disabled_properties=${this.filterValues.showDisabledProperties}`;
                url += `&show_properties_with_no_activity=${this.filterValues.showPropertiesWithNoActivity}`;
                url += `&properties=${this.filterProperties.length > 0 ? this.filterProperties.join(',') : 'none'}`;
                return url;
            },
            exportExcelHref() {
                let url = '/app/reports/income-detail/row-data/excel/';
                url += `?from_date=${this.startDateRequest}`;
                url += `&to_date=${this.endDateRequest}`;
                url += `&show_rooms=${this.filterValues.showRooms}`;
                url += `&show_disabled_properties=${this.filterValues.showDisabledProperties}`;
                url += `&show_properties_with_no_activity=${this.filterValues.showPropertiesWithNoActivity}`;
                url += `&properties=${this.filterProperties.length > 0 ? this.filterProperties.join(',') : 'none'}`;
                return url;
            },
            startDateRequest() {
                return format(this.dateRange.from, this.dateRequestFormat);
            },
            endDateRequest() {
                return format(this.dateRange.to, this.dateRequestFormat);
            },
            incomeDetailFilters() {
                return [
                    { labels: 'Show Rooms', key: `showRooms` },
                    {
                        labels: 'Show Disabled Properties',
                        key: `showDisabledProperties`,
                    },
                    {
                        labels: 'Show Properties with No Activity',
                        key: `showPropertiesWithNoActivity`,
                    },
                ];
            },
            isMobile() {
                const md = 768;
                return window.innerWidth < md || window.innerHeight < md;
            },
            reportSettings() {
                return {
                    name: 'Income Detail',
                    report_sysname: 'INCOME_DETAIL',
                    settings: [
                        {
                            type: DATE_PERIODS_INPUT,
                            value: 'this_month',
                            predefinedRanges: this.predefinedRanges,
                            label: 'Period',
                            name: 'from_date',
                        },
                        {
                            type: MULTI_SELECT_INPUT,
                            value: [],
                            name: 'properties',
                            options: this.filterPropertyOptions,
                            label: 'Only for Properties',
                            placeholder: 'All Properties',
                        },
                        {
                            type: SWITCH_INPUT,
                            value: false,
                            label: 'Show Rooms',
                            name: 'show_rooms',
                        },
                        {
                            type: SWITCH_INPUT,
                            value: false,
                            label: 'Show Disabled Properties',
                            name: 'show_disabled_properties',
                        },
                        {
                            type: SWITCH_INPUT,
                            value: false,
                            label: 'Show Properties With NO Activity',
                            name: 'show_properties_with_no_activity',
                        },
                    ],
                };
            },
        },
        mounted() {
            this.fetchProperties();
            this.fetchData();
            const grid = this.$refs.grid;
            this.gridApi = grid.gridOptions.api;
            this.onResize();
            window.addEventListener('resize', this.onResize);
        },
        beforeUnmount() {
            window.removeEventListener('resize', this.onResize);
        },
        beforeMount() {
            this.getRowStyle = ({ node }) => {
                if (node.rowPinned) {
                    return { 'font-weight': 'bold' };
                }
            };
            this.statusBar = {
                statusPanels: [
                    {
                        key: 'reportSummaryBar',
                        statusPanel: 'reportSummaryBar',
                        align: 'left',
                    },
                ],
            };
        },
        methods: {
            refresh() {
                this.fetchData();
            },
            async fetchData() {
                try {
                    this.busy = true;
                    const { data } = await API.get(REPORT_INCOME_DETAIL_ENDPOINT(), {
                        params: {
                            from_date: this.startDateRequest,
                            to_date: this.endDateRequest,
                            show_rooms: this.filterValues.showRooms,
                            show_disabled_properties: this.filterValues.showDisabledProperties,
                            show_properties_with_no_activity: this.filterValues.showPropertiesWithNoActivity,
                            properties: this.filterProperties.length > 0 ? this.filterProperties.join(',') : 'none',
                        },
                    });
                    const { properties, average, totals, pdf_cache_key } = data;
                    this.rowData = properties;
                    this.average = average;
                    this.totals = totals;
                    this.pdfCacheKey = pdf_cache_key;
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch data' });
                } finally {
                    this.busy = false;
                }
            },
            async fetchProperties() {
                const { data } = await API.get(REPORT_INCOME_DETAIL_PROPERTIES_ENDPOINT());
                this.filterPropertyOptions = data.map((item) => ({
                    value: !!item.room_id ? -item.room_id : item.property_id,
                    text: item.name,
                    disabled: item.disabled,
                }));
            },
            onResize() {
                this.windowWidth = window.innerWidth;
                this.windowHeight = window.innerHeight;
                const statusBarComponent = this.gridApi.getStatusPanel('reportSummaryBar');
                let componentInstance = statusBarComponent;
                if (statusBarComponent.getFrameworkComponentInstance) {
                    componentInstance = statusBarComponent.getFrameworkComponentInstance();
                }
                componentInstance.setVisible(this.isMobile);
                componentInstance.setTitle(this.title);
                componentInstance.setCaption(
                    'Invoices w/ depart dates outside of reporting period will be prorated by night.'
                );
            },
            handleDaterangeChange(value) {
                this.dateRange = { ...value };
                this.fetchData();
            },
        },
        watch: {
            filterProperties: {
                deep: true,
                handler() {
                    this.fetchData();
                },
            },
        },
    };
</script>

<style lang="scss">
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';

    .income-detail-dropdown {
        :deep(.buttons-wrapper) {
            width: 100%;
        }

        .income-detail-toggle {
            width: 245px;
            @include media-breakpoint-down(lg) {
                width: 270px;
            }
        }
    }
</style>
